import axios from 'axios';

const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
  refreshToken: async () => {
    const response = await api.post('/api/auth/refresh');
    return response.data;
  },
};

export const usersAPI = {
  getUsers: async (customerId) => {
    const url = customerId ? `/api/users?customer_id=${customerId}` : '/api/users';
    const response = await api.get(url);
    return response.data;
  },
  getUser: async (id) => {
    const response = await api.get(`/api/users/${id}`);
    return response.data;
  },
  createUser: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },
  updateUser: async (id, userData) => {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data;
  },
  deleteUser: async (id) => {
    await api.delete(`/api/users/${id}`);
  },
  getManagerView: async () => {
    const response = await api.get('/api/users/manager-view');
    return response.data;
  },
  getUserSkills: async (id) => {
    const response = await api.get(`/api/users/${id}/skills`);
    return response.data;
  },
  addUserSkill: async (userId, skillId) => {
    const response = await api.post(`/api/users/${userId}/skills`, { skill_id: skillId });
    return response.data;
  },
  removeUserSkill: async (userId, skillId) => {
    await api.delete(`/api/users/${userId}/skills/${skillId}`);
  },
};

export const customersAPI = {
  getCustomers: async () => {
    const response = await api.get('/api/customers');
    return response.data;
  },
  getCustomer: async (id) => {
    const response = await api.get(`/api/customers/${id}`);
    return response.data;
  },
  createCustomer: async (customerData) => {
    const response = await api.post('/api/customers', customerData);
    return response.data;
  },
  updateCustomer: async (id, customerData) => {
    const response = await api.put(`/api/customers/${id}`, customerData);
    return response.data;
  },
  deleteCustomer: async (id) => {
    await api.delete(`/api/customers/${id}`);
  },
  getCustomerCDRs: async (id) => {
    const response = await api.get(`/api/customers/${id}/cdrs`);
    return response.data;
  },
  getCustomerUsers: async (id) => {
    const response = await api.get(`/api/customers/${id}/users`);
    return response.data;
  },
};

export const cdrAPI = {
  getCDRs: async (params = {}) => {
    const response = await api.get('/api/cdr', { params });
    return response.data;
  },
  getCDR: async (id) => {
    const response = await api.get(`/api/cdr/${id}`);
    return response.data;
  },
  createCDR: async (cdrData) => {
    const response = await api.post('/api/cdr', cdrData);
    return response.data;
  },
  updateCDR: async (id, cdrData) => {
    const response = await api.put(`/api/cdr/${id}`, cdrData);
    return response.data;
  },
  deleteCDR: async (id) => {
    await api.delete(`/api/cdr/${id}`);
  },
  getStats: async (params = {}) => {
    const response = await api.get('/api/cdr/stats/summary', { params });
    return response.data;
  },
  getTimeline: async (params = {}) => {
    const response = await api.get('/api/cdr/stats/timeline', { params });
    return response.data;
  },
  exportCSV: async (params = {}) => {
    const response = await api.get('/api/cdr/export/csv', { params, responseType: 'blob' });
    return response.data;
  },
};

export const extensionsAPI = {
  register: async (data) => {
    const response = await api.post('/api/extensions/register', data);
    return response.data;
  },
  unregister: async (data) => {
    const response = await api.post('/api/extensions/unregister', data);
    return response.data;
  },
  getRegistered: async () => {
    const response = await api.get('/api/extensions/registered');
    return response.data;
  },
  getRegistry: async (params = {}) => {
    const response = await api.get('/api/extensions/registry', { params });
    return response.data;
  },
  getRegistryStatsBySkill: async () => {
    const response = await api.get('/api/extensions/registry/stats-by-skill');
    return response.data;
  },
  getCallEventStatsBySkill: async () => {
    const response = await api.get('/api/extensions/call-event-stats-by-skill');
    return response.data;
  },
  callAnswer: async (data) => {
    const response = await api.post('/api/extensions/call-answer', data);
    return response.data;
  },
  callRinging: async (data) => {
    const response = await api.post('/api/extensions/call-ringing', data);
    return response.data;
  },
  callHangup: async (data) => {
    const response = await api.post('/api/extensions/call-hangup', data);
    return response.data;
  },
};

export const skillsAPI = {
  getSkills: async (customerId) => {
    const url = customerId ? `/api/skills?customer_id=${customerId}` : '/api/skills';
    const response = await api.get(url);
    return response.data;
  },
  getSkill: async (id) => {
    const response = await api.get(`/api/skills/${id}`);
    return response.data;
  },
  createSkill: async (data) => {
    const response = await api.post('/api/skills', data);
    return response.data;
  },
  updateSkill: async (id, data) => {
    const response = await api.put(`/api/skills/${id}`, data);
    return response.data;
  },
  deleteSkill: async (id) => {
    await api.delete(`/api/skills/${id}`);
  },
};

export const groupsAPI = {
  getGroups: async (customerId) => {
    const url = customerId ? `/api/groups?customer_id=${customerId}` : '/api/groups';
    const response = await api.get(url);
    return response.data;
  },
  getGroup: async (id) => {
    const response = await api.get(`/api/groups/${id}`);
    return response.data;
  },
  createGroup: async (data) => {
    const response = await api.post('/api/groups', data);
    return response.data;
  },
  updateGroup: async (id, data) => {
    const response = await api.put(`/api/groups/${id}`, data);
    return response.data;
  },
  deleteGroup: async (id) => {
    await api.delete(`/api/groups/${id}`);
  },
  getMembers: async (id) => {
    const response = await api.get(`/api/groups/${id}/members`);
    return response.data;
  },
  addMember: async (groupId, userId) => {
    const response = await api.post(`/api/groups/${groupId}/members`, { user_id: userId });
    return response.data;
  },
  removeMember: async (groupId, userId) => {
    await api.delete(`/api/groups/${groupId}/members/${userId}`);
  },
};

export const registryHistoryAPI = {
  getHistory: async (params = {}) => {
    const response = await api.get('/api/registry-history', { params });
    return response.data;
  },
};

export default api;
